import React, { useState, useEffect, useRef, useCallback } from 'react';
import FirmaCanvas from '../components/pazienti/FirmaCanvas';
import intestazioniTablet from '../utils/chiaveDispositivo';
import '../styles/Tablet.css';
import ModulisticaWizard from '../components/pazienti/ModulisticaWizard';
import TimbraturaTablet from '../components/TimbraturaTablet';
import PressioneLunga from '../components/PressioneLunga';
import QuestionarioTablet from '../components/QuestionarioTablet';
import { useRicaricaPermessa } from '../utils/versioneInLinea';

/**
 * Tablet.jsx — la pagina che gira sul tablet in sala d'attesa.
 *
 * NON usa il Layout del gestionale e non ha alcun collegamento verso
 * altre pagine: il dispositivo resta in mano ai pazienti, e da qui non
 * si deve poter arrivare da nessun'altra parte.
 *
 * Si identifica con la chiave del dispositivo, salvata nel browser una
 * volta sola. Non c'e' nome utente, non c'e' accesso: il tablet vede
 * una sessione alla volta e per pochi minuti.
 */

const CHIAVE_SALVATA = 'rimedichub_dispositivo';
const INTERVALLO_MS = 4000;

/* Quanto resta a video il «Grazie» dopo un'accettazione (Alex, 2/10/2026:
   30 secondi) e dopo un documento firmato (6 secondi). */
const GRAZIE_ACCETTAZIONE_MS = 30 * 1000;
const GRAZIE_DOCUMENTO_MS = 6000;
/* Le prove dei percorsi (scenario 15, casi 9e-9h) accorciano questi tempi
   e l'interrogazione con window.__rimedichubTempiTablet, messo prima del
   caricamento, come fanno con i tempi di utils/versioneInLinea.js:
   aspettare trenta secondi veri non e' una prova, e' una pausa. Mai sotto i
   100 ms; senza l'oggetto, i valori di sempre. */
const tempoTablet = (nome, predefinito) => {
    const v = Number(window.__rimedichubTempiTablet?.[nome]);
    return Number.isFinite(v) && v >= 100 ? v : predefinito;
};

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(v);
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

/* Sostituzione dei segnaposto {{ }} nel testo del modello */
const costruisciMappa = (s) => ({
    'paziente.nome': s.paziente_nome || '',
    'paziente.cognome': s.paziente_cognome || '',
    'paziente.nome_completo': `${s.paziente_nome || ''} ${s.paziente_cognome || ''}`.trim(),
    'paziente.codice_fiscale': s.codice_fiscale || '',
    'paziente.data_nascita': dataIta(s.data_nascita),
    'paziente.luogo_nascita': s.luogo_nascita || '',
    'paziente.indirizzo': s.indirizzo || '',
    'paziente.citta': s.citta || '',
    'paziente.cap': s.cap || '',
    'paziente.provincia': s.provincia || '',
    'paziente.telefono': s.telefono || '',
    'paziente.email': s.email || '',
    'medico.nome': s.medico?.nome || '',
    'medico.cognome': s.medico?.cognome || '',
    'medico.titolo': s.medico?.titolo || '',
    'medico.nome_completo': s.medico
        ? `${s.medico.titolo || ''} ${s.medico.nome || ''} ${s.medico.cognome || ''}`.trim() : '',
    'data.oggi': new Date().toLocaleDateString('it-IT'),
});

const sostituisci = (testo, mappa) =>
    String(testo || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (intero, chiave) =>
        mappa[chiave] !== undefined ? mappa[chiave] : intero);

/* Grassetto e corsivo del modello */
const conFormattazione = (testo) => {
    const pezzi = String(testo).split(/(\*\*[^*]+\*\*|_[^_]+_)/g).filter((p) => p !== '');
    return pezzi.map((pezzo, i) => {
        if (pezzo.startsWith('**') && pezzo.endsWith('**')) {
            return <b key={i}>{pezzo.slice(2, -2)}</b>;
        }
        if (pezzo.startsWith('_') && pezzo.endsWith('_')) {
            return <i key={i}>{pezzo.slice(1, -1)}</i>;
        }
        return <span key={i}>{pezzo}</span>;
    });
};

const Tablet = () => {
    const [chiave, setChiave] = useState(() => localStorage.getItem(CHIAVE_SALVATA) || '');
    const [chiaveInserita, setChiaveInserita] = useState('');
    const [struttura, setStruttura] = useState('');

    /* Come si chiama questa clinica lo dice il server: qui non sta piu'
       scritto. La rotta non e' autenticata perche' la usa anche il tablet,
       dove nessun utente e' collegato. */
    useEffect(() => {
        fetch('/api/struttura')
            .then((r) => r.json())
            .then((d) => setStruttura(d?.data?.ragione_sociale || ''))
            .catch(() => {});
    }, []);

    const [dispositivo, setDispositivo] = useState(null);
    const [sessione, setSessione] = useState(null);
    const [risposte, setRisposte] = useState({});
    const ultimeSalvate = useRef('{}');
    const [firmaPaziente, setFirmaPaziente] = useState(null);
    const [firmaMedico, setFirmaMedico] = useState(null);
    const [passo, setPasso] = useState('lettura');   // lettura | firma | medico | fatto
    const [errore, setErrore] = useState(null);
    const [invio, setInvio] = useState(false);
    const codiceCorrente = useRef(null);
    /* IL CONTO DEL «GRAZIE» DI UN DOCUMENTO E' UNO SOLO (trovato dal beta il
       18/10/2026, scenario 15 dei percorsi: il tablet tornava all'attesa
       58 ms dopo la seconda firma). Il conto armato dalla prima firma non si
       fermava mai: se scattava dopo la firma del documento dopo, con
       codiceCorrente di nuovo vuoto, tagliava il «Grazie» del secondo. In
       clinica, con 6 secondi, bastava firmare due documenti a meno di 6
       secondi l'uno dall'altro. Si ferma quando arriva un documento nuovo e
       quando si arma quello della firma dopo. */
    const contoGrazie = useRef(null);
    const fermaContoGrazie = () => {
        if (contoGrazie.current) clearTimeout(contoGrazie.current);
        contoGrazie.current = null;
    };
    useEffect(() => fermaContoGrazie, []);

    /* OGNI RISPOSTA SI METTE DA PARTE (26/09/2026, migration 254): se la
       pagina si chiude a meta' di un consenso, riaprendola le risposte ci
       sono ancora. Un attimo dopo l'ultimo tocco, e solo se sono cambiate;
       se il salvataggio non riesce il consenso va avanti lo stesso: la
       ripresa e' un aiuto, non una condizione. Le firme non passano di
       qui. I questionari lo fanno da se' (QuestionarioTablet). */
    useEffect(() => {
        if (!sessione || sessione.tipo_sessione === 'questionario' || !chiave) return undefined;
        const testo = JSON.stringify(risposte);
        if (testo === ultimeSalvate.current) return undefined;
        const t = setTimeout(() => {
            fetch('/api/firma-tablet/risposte', {
                method: 'POST',
                headers: intestazioniTablet(chiave, true),
                body: JSON.stringify({ codice: sessione.codice, risposte }),
            }).then((r) => { if (r.ok) ultimeSalvate.current = testo; }).catch(() => {});
        }, 400);
        return () => clearTimeout(t);
    }, [risposte, sessione, chiave]);

    // Modulo anagrafico: 'no' = non aperto
    const [anagrafica, setAnagrafica] = useState('no');
    /* La timbratura vive accanto all'accettazione, non dentro: sono due
       lavori diversi fatti dalle stesse persone sullo stesso apparecchio.
       Se il dispositivo non e' abilitato, il pulsante c'e' ma il server
       risponde no - e il componente lo dice. */
    const [timbratura, setTimbratura] = useState(false);
    const [questionarioFatto, setQuestionarioFatto] = useState(null);
    // 'completo' | 'prelievi': al punto prelievi non serve il consenso
    // alle immagini, e la segreteria deve saperlo dall'inizio.
    const [tipoAccettazione, setTipoAccettazione] = useState('completo');
    /* La compilazione lasciata a meta', quando la segreteria preme
       «Riprendi». Arriva dal server dentro il giro di polling, e non
       resta nel browser: il tablet non tiene niente di suo, cosi' un
       apparecchio ripreso in mano da un'altra persona non ha in memoria
       quello di prima. */
    const [bozza, setBozza] = useState(null);

    /* IL RITORNO ALL'ATTESA DAL «GRAZIE» DELL'ACCETTAZIONE: una strada sola,
       per il conto dei 30 secondi e per un documento che arriva prima (vedi
       sotto). Azzera tutto quello che la pratica appena chiusa aveva
       toccato qui: la bozza ripresa (porta l'anagrafica) e il tipo di
       accettazione. Il riferimento serve all'interrogazione, che non
       rinasce a ogni cambio di `anagrafica`. */
    const anagraficaOra = useRef(anagrafica);
    anagraficaOra.current = anagrafica;
    const chiudiGrazieAccettazione = useCallback(() => {
        setBozza(null);
        setTipoAccettazione('completo');
        setAnagrafica('no');
    }, []);

    /* ---------- interrogazione periodica ---------- */
    const controlla = useCallback(async () => {
        if (!chiave) return;
        try {
            const r = await fetch('/api/firma-tablet/attesa',
                                  { headers: intestazioniTablet(chiave) });
            const d = await r.json();

            if (!d.success) {
                setErrore(d.message || 'Dispositivo non riconosciuto');
                if (r.status === 401) {
                    localStorage.removeItem(CHIAVE_SALVATA);
                    setChiave('');
                }
                return;
            }

            setErrore(null);
            setDispositivo(d.data.dispositivo);

            const nuova = d.data.sessione;

            /* LA RIPRESA ARRIVA DAL SERVER, MAI DA UNA MEMORIA LOCALE.
               Vale anche a tablet fermo: chi si stava accettando da solo
               non ha una sessione, e la sua bozza torna qui. Il tablet
               non chiede mai «hai una bozza per me» e non ne vede mai un
               elenco: riceve quella che la segreteria ha rimandato, e
               basta. */
            if (d.data.bozza) {
                setBozza(d.data.bozza);
                setTipoAccettazione(d.data.bozza.dati?.tipo_accettazione === 'prelievi'
                    ? 'prelievi' : 'completo');
                setAnagrafica('si');
            }

            // Nessun documento: si torna in attesa, ma non si cancella
            // quello appena firmato mentre il messaggio e' a video.
            if (!nuova) {
                if (codiceCorrente.current && passo !== 'fatto') {
                    codiceCorrente.current = null;
                    setSessione(null);
                }
                return;
            }

            // Documento nuovo: si azzera tutto
            if (nuova.codice !== codiceCorrente.current) {
                /* Un documento mandato mentre e' a video il «Grazie»
                   dell'accettazione lo chiude, come chiude quello di un
                   documento firmato (revisione del 2/10/2026): il ramo del
                   «Grazie» viene prima di quello della sessione, e il
                   documento sarebbe rimasto nascosto fino alla fine dei 30
                   secondi - chi l'ha mandato vede il tablet fermo e lo
                   rimanda, e la seconda sessione annulla la prima. Con una
                   bozza nella stessa risposta il «Grazie» l'ha gia' chiuso
                   lei, qui sopra: non la si toglie. */
                if (anagraficaOra.current === 'fatto' && !d.data.bozza) chiudiGrazieAccettazione();
                fermaContoGrazie();
                codiceCorrente.current = nuova.codice;
                setSessione(nuova);
                /* Le risposte gia' date, se la pagina si era chiusa a meta'
                   (migration 254). Le firme no: si rifanno. */
                ultimeSalvate.current = JSON.stringify(nuova.risposte_bozza || {});
                setRisposte(nuova.risposte_bozza || {});
                setFirmaPaziente(null);
                setFirmaMedico(null);
                setPasso('lettura');
            }
        } catch (e) {
            console.error('Interrogazione:', e);
        }
    }, [chiave, passo, chiudiGrazieAccettazione]);

    useEffect(() => {
        if (!chiave) return;
        controlla();
        const t = setInterval(controlla, tempoTablet('interrogazione', INTERVALLO_MS));
        return () => clearInterval(t);
    }, [chiave, controlla]);

    /* LA VERSIONE NUOVA SI PRENDE SOLO DALL'ATTESA (Alex, 2/10/2026; vedi
       utils/versioneInLinea.js). Il predefinito, sul tablet, e' non
       ricaricare mai: lo si permette solo qui, sulla schermata in cui il
       paziente non ha ancora cominciato niente - nessun documento, nessuna
       accettazione o bozza, nessuna timbratura, nessun «Grazie» a video.
       Sta prima dei ritorni anticipati qui sotto perche' e' un hook. */
    const inAttesa = !!chiave && !sessione && anagrafica === 'no' && !timbratura
        && !questionarioFatto && passo !== 'fatto' && !bozza;
    useRicaricaPermessa(inAttesa);

    /* IL «GRAZIE» DELL'ACCETTAZIONE TORNA DA SOLO ALL'ATTESA (Alex,
       2/10/2026), come gli altri due «Grazie». Senza un conto ci resterebbe
       finche' qualcuno non ricarica la pagina o non arriva un documento: sul
       «Grazie» non c'e' niente da toccare, e un tablet fuori dall'attesa non
       prende la versione nuova. Trenta secondi, il tempo di leggerlo.
       E' anche RISERVATEZZA: il paziente dopo non deve trovare niente di
       quello prima, quindi tornando all'attesa si azzera tutto quello che
       la pratica appena chiusa aveva toccato qui - la bozza ripresa (porta
       l'anagrafica), il tipo di accettazione. Il modulo, con i dati e le
       firme, si e' gia' smontato al «Grazie». Se nel frattempo arriva una
       bozza da riprendere o un documento, il «Grazie» lascia il posto a lei
       o a lui e il conto si ferma (la pulizia dell'effetto). */
    useEffect(() => {
        if (anagrafica !== 'fatto') return undefined;
        const t = setTimeout(chiudiGrazieAccettazione,
                             tempoTablet('grazieAccettazione', GRAZIE_ACCETTAZIONE_MS));
        return () => clearTimeout(t);
    }, [anagrafica, chiudiGrazieAccettazione]);

    /* ---------- configurazione iniziale ---------- */
    if (!chiave) {
        return (
            <div className="tab-configurazione">
                <div className="tab-riquadro">
                    <h1>Configurazione del dispositivo</h1>
                    <p>
                        Inserisci la chiave che ti è stata fornita. Serve una volta sola:
                        resta salvata su questo apparecchio.
                    </p>
                    <input
                        type="password"
                        value={chiaveInserita}
                        onChange={(e) => setChiaveInserita(e.target.value)}
                        placeholder="Chiave del dispositivo"
                        autoFocus
                    />
                    {errore && <p className="tab-errore">{errore}</p>}
                    <button
                        onClick={() => {
                            const c = chiaveInserita.trim();
                            if (!c) return;
                            localStorage.setItem(CHIAVE_SALVATA, c);
                            setChiave(c);
                            setChiaveInserita('');
                        }}
                        disabled={!chiaveInserita.trim()}
                    >
                        Attiva
                    </button>
                </div>
            </div>
        );
    }

    /* ---------- documento firmato ---------- */
    if (passo === 'fatto') {
        return (
            <div className="tab-esito">
                <div className="tab-segno">✓</div>
                <h1>Grazie</h1>
                <p>Il documento è stato firmato e archiviato.</p>
                <p className="tab-minuto">Può riconsegnare il dispositivo alla segreteria.</p>
            </div>
        );
    }

    /* ---------- questionario consegnato ---------- */
    if (questionarioFatto) {
        return (
            <div className="tab-esito">
                <div className="tab-segno">✓</div>
                <h1>Grazie</h1>
                <p>Il questionario è stato inviato.</p>
                <p className="tab-minuto">
                    {questionarioFatto.risposte} risposte su {questionarioFatto.totale}.
                    Può riconsegnare il dispositivo alla segreteria.
                </p>
            </div>
        );
    }

    /* ---------- anagrafica inviata ---------- */
    if (anagrafica === 'fatto') {
        return (
            <div className="tab-esito">
                <div className="tab-segno">✓</div>
                <h1>Grazie</h1>
                <p>I moduli firmati sono stati inviati alla segreteria.</p>
                <p className="tab-minuto">Può riconsegnare il dispositivo.</p>
            </div>
        );
    }

    /* ---------- timbratura ---------- */
    if (timbratura) {
        return <TimbraturaTablet chiave={chiave} onChiudi={() => setTimbratura(false)} />;
    }

    /* ---------- accettazione: il percorso completo ---------- */
    if (anagrafica === 'si') {
        return (
            <ModulisticaWizard
                /* Una bozza ripresa e' una compilazione nuova: senza
                   `key` React riuserebbe l'istanza di prima e lo stato
                   nato da useState resterebbe quello vecchio - lo stesso
                   difetto gia' visto sulla pratica. */
                key={bozza ? `bozza-${bozza.passo}-${bozza.aggiornata || ''}` : 'nuova'}
                chiaveTablet={chiave}
                bozza={bozza ? { passo: bozza.passo, ...(bozza.dati || {}) } : null}
                modalita={tipoAccettazione === 'prelievi' ? 'prelievi' : 'completo'}
                /* IL «GRAZIE» NON COMPARIVA (misurato il 2/10/2026 dallo
                   scenario 15: la pratica arrivava, 201, e il tablet tornava
                   subito all'attesa). Il modulo, finito l'invio, chiama
                   onSuccess e subito dopo onClose, nello stesso giro: con
                   React 18 i due aggiornamenti si raccolgono insieme e
                   vinceva l'ultimo, 'no'. La chiusura non cancella un
                   «Grazie» gia' deciso; lo toglie il conto qui sopra. */
                onClose={() => {
                    setBozza(null);
                    setAnagrafica((a) => (a === 'fatto' ? a : 'no'));
                }}
                onSuccess={() => { setBozza(null); setAnagrafica('fatto'); }}
            />
        );
    }

    /* ---------- pratica di un paziente gia' in archivio ----------
       Non c'e' un documento da firmare: c'e' l'anagrafica da far
       verificare e i quattro moduli, cioe' lo stesso percorso
       dell'accettazione ma su un paziente che esiste. */
    if (sessione && sessione.tipo_sessione === 'pratica' && sessione.paziente) {
        return (
            <ModulisticaWizard
                /* LA `key` LEGATA ALLA SESSIONE.
                   Senza, React riusa la stessa istanza quando arriva una
                   sessione nuova: lo stato nato da useState resta quello
                   del paziente di prima, mentre il codice della sessione
                   arriva dalle props ed e' gia' quello nuovo. I dati e le
                   firme di A finivano nella cartella di B. Con la `key`
                   il componente si smonta e ricomincia da zero. */
                key={sessione.codice}
                chiaveTablet={chiave}
                codiceSessione={sessione.codice}
                bozza={sessione.bozza
                    ? { passo: sessione.bozza.passo, ...(sessione.bozza.dati || {}) }
                    : null}
                pazienteEsistente={sessione.paziente}
                modalita={sessione.paziente.tipo_accettazione === 'prelievi'
                    ? 'prelievi' : 'completo'}
                onClose={() => { codiceCorrente.current = null; setSessione(null); }}
                onSuccess={() => {
                    codiceCorrente.current = null;
                    setSessione(null);
                    setPasso('fatto');
                    setTimeout(() => setPasso('lettura'), 8000);
                }}
            />
        );
    }

    /* ---------- questionario ----------
       Una sessione di questionario non ha blocchi da leggere ne' una firma
       da raccogliere: e' un'altra cosa, e si comporta da un'altra parte.
       Il ramo sta qui sopra l'attesa perche' sotto ci sono i rami del
       documento, che darebbero per scontato `sessione.contenuto`. */
    if (sessione && sessione.tipo_sessione === 'questionario') {
        return (
            <QuestionarioTablet
                /* come sopra: una sessione nuova, un componente nuovo */
                key={sessione.codice}
                chiave={chiave}
                sessione={sessione}
                onFatto={(esito) => {
                    setQuestionarioFatto(esito);
                    codiceCorrente.current = null;
                    setSessione(null);
                    setTimeout(() => setQuestionarioFatto(null), 8000);
                }}
            />
        );
    }

    /* ---------- attesa ---------- */
    if (!sessione) {
        return (
            <div className="tab-attesa">
                <h1>{struttura || '\u00a0'}</h1>
                <p>Nessun documento da firmare al momento.</p>
                <div className="tab-accettazioni">
                    <button className="tab-nuovo"
                            onClick={() => { setTipoAccettazione('completo'); setAnagrafica('si'); }}>
                        Accettazione paziente
                    </button>
                    <button className="tab-nuovo"
                            onClick={() => { setTipoAccettazione('prelievi'); setAnagrafica('si'); }}>
                        Accettazione punto prelievi
                    </button>
                    {/* Tenere premuto due secondi (Alex, 26/09/2026): e' la
                        schermata che ha in mano il paziente, e un tocco per
                        sbaglio apriva la timbratura. */}
                    <PressioneLunga className="tab-nuovo tab-nuovo--timbra"
                                    onCompletata={() => setTimbratura(true)}
                                    suggerimento="Tieni premuto 2 secondi">
                        Timbra entrata / uscita
                    </PressioneLunga>
                </div>
                {errore && <p className="tab-errore">{errore}</p>}
                <p className="tab-minuto">{dispositivo}</p>
            </div>
        );
    }

    /* ---------- documento ---------- */
    const mappa = costruisciMappa(sessione);
    const blocchi = sessione.contenuto?.contenuto?.blocchi || [];

    const obbligatoriMancanti = blocchi
        .map((b, i) => ({ b, i }))
        .filter(({ b, i }) => b.tipo === 'consenso' && b.obbligatorio && risposte[i] !== true);

    const invia = async () => {
        try {
            setErrore(null);
            setInvio(true);
            const r = await fetch('/api/firma-tablet/firma', {
                method: 'POST',
                headers: intestazioniTablet(chiave, true),
                body: JSON.stringify({
                    codice: sessione.codice,
                    risposte,
                    firma_base64: firmaPaziente,
                    firma_medico_base64: firmaMedico,
                }),
            });
            const d = await r.json();
            if (!d.success) {
                setErrore(d.message || 'Errore nella firma');
                return;
            }
            setPasso('fatto');
            codiceCorrente.current = null;
            /* Il messaggio resta a video qualche secondo, poi si torna in
               attesa da soli: nessuno deve toccare niente. Tornando si
               azzerano anche risposte e firme (revisione del 2/10/2026, il
               principio del «Grazie» dell'accettazione): prima restavano
               nello stato del tablet, non a video, fino al documento dopo.
               Un documento arrivato nel frattempo ha gia' preso il posto di
               questo (l'interrogazione: codice nuovo, risposte e firme
               sue): qui non si tocca, altrimenti spariva dallo schermo e,
               col suo codice gia' visto, non tornava piu'. */
            fermaContoGrazie();
            contoGrazie.current = setTimeout(() => {
                contoGrazie.current = null;
                if (codiceCorrente.current) return;
                setSessione(null);
                setPasso('lettura');
                setRisposte({});
                setFirmaPaziente(null);
                setFirmaMedico(null);
            }, tempoTablet('grazieDocumento', GRAZIE_DOCUMENTO_MS));
        } catch (e) {
            setErrore('Errore di collegamento. Riprovare.');
        } finally {
            setInvio(false);
        }
    };

    return (
        <div className="tab-documento">
            <div className="tab-testata">
                <span className="tab-titolo">{sessione.titolo}</span>
                <span className="tab-paziente">
                    {sessione.paziente_nome} {sessione.paziente_cognome}
                </span>
            </div>

            <div className="tab-corpo">
                {passo === 'lettura' && (
                    <>
                        {blocchi.map((b, i) => {
                            if (b.tipo === 'sezione') {
                                return <h2 key={i} className="tab-sezione">
                                    {sostituisci(b.testo, mappa)}
                                </h2>;
                            }
                            if (b.tipo === 'paragrafo') {
                                return <p key={i} className="tab-paragrafo">
                                    {conFormattazione(sostituisci(b.testo, mappa))}
                                </p>;
                            }
                            if (b.tipo === 'elenco') {
                                const Lista = b.ordinato ? 'ol' : 'ul';
                                return (
                                    <Lista key={i} className="tab-elenco">
                                        {(b.voci || []).map((v, k) => (
                                            <li key={k}>{conFormattazione(sostituisci(v, mappa))}</li>
                                        ))}
                                    </Lista>
                                );
                            }
                            if (b.tipo === 'consenso') {
                                const scelto = risposte[i];
                                return (
                                    <div key={i} className={`tab-consenso ${b.obbligatorio ? 'obbligatorio' : ''}`}>
                                        <p className="tab-consenso-testo">
                                            {sostituisci(b.etichetta, mappa)}
                                            {b.obbligatorio && <span className="tab-richiesto"> — richiesto</span>}
                                        </p>
                                        {b.descrizione && (
                                            <p className="tab-consenso-nota">{sostituisci(b.descrizione, mappa)}</p>
                                        )}
                                        <div className="tab-scelte">
                                            <button
                                                className={scelto === true ? 'scelto si' : ''}
                                                onClick={() => setRisposte({ ...risposte, [i]: true })}
                                            >
                                                Acconsento
                                            </button>
                                            <button
                                                className={scelto === false ? 'scelto no' : ''}
                                                onClick={() => setRisposte({ ...risposte, [i]: false })}
                                            >
                                                Non acconsento
                                            </button>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}

                        <div className="tab-azioni">
                            {obbligatoriMancanti.length > 0 && (
                                <p className="tab-avviso">
                                    Per proseguire occorre rispondere ai punti contrassegnati
                                    come richiesti.
                                </p>
                            )}
                            <button
                                className="tab-avanti"
                                onClick={() => setPasso('firma')}
                                disabled={obbligatoriMancanti.length > 0}
                            >
                                Ho letto, procedo alla firma
                            </button>
                        </div>
                    </>
                )}

                {passo === 'firma' && (
                    <div className="tab-firma">
                        <h2>Firma del paziente</h2>
                        <p className="tab-minuto">
                            {sessione.paziente_nome} {sessione.paziente_cognome} — firmi nello
                            spazio sottostante.
                        </p>
                        <FirmaCanvas valore={firmaPaziente} onChange={setFirmaPaziente} />
                        {errore && <p className="tab-errore">{errore}</p>}
                        <div className="tab-azioni">
                            <button className="tab-indietro" onClick={() => setPasso('lettura')}>
                                Torna al testo
                            </button>
                            <button
                                className="tab-avanti"
                                onClick={() => setPasso('medico')}
                                disabled={!firmaPaziente}
                            >
                                Avanti
                            </button>
                        </div>
                    </div>
                )}

                {passo === 'medico' && (
                    <div className="tab-firma">
                        <h2>Firma del medico</h2>
                        <p className="tab-minuto">
                            Consegnare il dispositivo al medico
                            {sessione.medico
                                ? ` — ${sessione.medico.titolo || ''} ${sessione.medico.cognome || ''}`
                                : ''}.
                        </p>
                        <FirmaCanvas valore={firmaMedico} onChange={setFirmaMedico} />
                        {errore && <p className="tab-errore">{errore}</p>}
                        <div className="tab-azioni">
                            <button className="tab-indietro" onClick={() => setPasso('firma')}>
                                Indietro
                            </button>
                            <button className="tab-avanti" onClick={invia} disabled={invio}>
                                {invio ? 'Invio…' : 'Concludi e archivia'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tablet;
